// @flow

import gql from 'graphql-tag';
import React, { type Node } from 'react';
import { get, noop } from 'lodash';

import { Mutation, Query } from '@apollo/react-components';

import { type AccountChoiceType } from '@zapier/team/src/types';

type ChilrenProps = {
  isLoading: boolean,
  isWorking: boolean,
  onJoinTeam: AccountChoiceType => void,
  joinableAccounts: Array<AccountChoiceType>,
};

type Props = {
  children: ChilrenProps => Node,
  onCreateNotification?: ({
    isSuccess?: boolean,
    message: string,
  }) => () => void,
  skip?: boolean,
};

const joinableAccountsQuery = gql`
  query WithAccountAccess {
    joinableAccounts {
      accessRequested
      canJoin
      id
      isEmailVerificationRequired
      imageUrl
      name
    }
  }
`;

const joinTeamMutation = gql`
  mutation JoinTeam($accountId: Int!) {
    joinAccount(accountId: $accountId) {
      id
      name
    }
  }
`;

const requestAccessMutation = gql`
  mutation RequestAccess($accountId: Int!) {
    requestAccess(accountId: $accountId) {
      createdAt
    }
  }
`;

const WithTeamAccess = (props: Props) => {
  const createNotification = props.onCreateNotification || noop;

  const handleJoinTeamError = error => {
    // When attempting to join a team that has SAML configured the server will respond with
    // the SAML SSO url so that the user can confirm they have an identity configured in
    // the identity provider before we allow them to join the account
    try {
      const {
        ssoUrl,
      } = error.graphQLErrors[0].extensions.exception.response.body;
      location.href = ssoUrl;
    } catch (e) {
      return createNotification({
        message: 'Error joining team. Please Try Again.',
      });
    }

    return null;
  };

  return (
    <Query query={joinableAccountsQuery} skip={props.skip}>
      {queryResults => (
        <Mutation
          awaitRefetchQueries={true}
          mutation={joinTeamMutation}
          // reload the page so that all of the auth shows up correctly
          // throughout the whole site
          onCompleted={() => location.reload()}
          onError={handleJoinTeamError}
        >
          {(joinTeam, joinTeamResults) => (
            <Mutation
              awaitRefetchQueries={true}
              mutation={requestAccessMutation}
              onError={createNotification({
                message: 'Error Requesting Access Team. Please Try Again.',
              })}
              refetchQueries={() => [
                {
                  query: joinableAccountsQuery,
                },
              ]}
            >
              {(requestAccess, requestAccessResults) => {
                const isWorking =
                  joinTeamResults.loading || requestAccessResults.loading;

                const handleJoinTeam = team => {
                  if (team.canJoin) {
                    joinTeam({ variables: { accountId: team.id } });
                  } else {
                    requestAccess({
                      variables: { accountId: team.id },
                    });
                  }
                };

                return props.children({
                  isLoading: queryResults.loading,
                  isWorking,
                  joinableAccounts: get(
                    queryResults,
                    ['data', 'joinableAccounts'],
                    []
                  ),
                  onJoinTeam: handleJoinTeam,
                });
              }}
            </Mutation>
          )}
        </Mutation>
      )}
    </Query>
  );
};

export default WithTeamAccess;
